/**
 * Sprint 208 Revised+ — Privacy Shield Hits · Last 24h tile.
 *
 * Counts every 403 the middleware fired on a parent attempting to read
 * a teen chat log. Zero is the happy path; any non-zero number means
 * either a buggy client OR a probe we should investigate.
 *
 * Mounts on the Command Centre below the Active Launch Funnel.
 */
import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, RefreshCcw, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const Sparkline = ({ buckets = [], stroke = '#dc2626' }) => {
  const max = Math.max(1, ...buckets);
  const w = 220, h = 36;
  const step = buckets.length > 1 ? w / (buckets.length - 1) : 0;
  const pts = buckets
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="shield-hits-sparkline">
      <polyline fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      {buckets.map((v, i) => (
        <circle key={i} cx={i * step} cy={h - (v / max) * (h - 4) - 2} r={v > 0 ? 2.2 : 1.2} fill={v > 0 ? stroke : '#d4d4d8'} />
      ))}
    </svg>
  );
};

const PrivacyShieldHitsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request('/api/onboarding/metrics/privacy-shield');
      setData(resp);
    } catch (e) {
      if ((e?.message || '').includes('admin_only') || (e?.message || '').includes('403')) {
        setData('hidden');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (data === 'hidden') return null;

  const total = loading ? null : (data?.total_24h ?? 0);
  const stroke = (total ?? 0) > 0 ? '#dc2626' : '#10b981'; // red on hits, green on zero
  const healthy = total === 0;

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-center gap-5"
      data-testid="shield-hits-card"
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${healthy ? 'bg-emerald-50' : 'bg-red-50'}`}>
          {healthy
            ? <Shield size={18} className="text-emerald-600" />
            : <AlertTriangle size={18} className="text-red-600" />}
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">🛡️ Privacy Shield Hits · Last 24h</h3>
          <p className="text-xs text-gray-500">Each hit = a parent JWT blocked from reading a teen chat log</p>
        </div>
      </div>

      <div className="flex items-baseline gap-1.5" data-testid="shield-hits-total">
        <span className={`text-3xl font-bold ${healthy ? 'text-emerald-600' : 'text-red-600'}`}>
          {loading ? '—' : total}
        </span>
        <span className="text-xs text-gray-500 font-medium">
          {total === 0 ? 'zero — happy path' : total === 1 ? 'hit' : 'hits'}
        </span>
      </div>

      <div className="flex-1 min-w-[220px]">
        {loading ? (
          <div className="h-9 flex items-center text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> loading…
          </div>
        ) : (
          <Sparkline buckets={data?.sparkline || []} stroke={stroke} />
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 max-w-md" data-testid="shield-hits-path-chips">
        {Object.entries(data?.by_path || {}).slice(0, 4).map(([p, n]) => (
          <span
            key={p}
            className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100"
            title={p}
            data-testid={`shield-hits-chip-${p.replace(/\W+/g,'_')}`}
          >
            {p} · {n}
          </span>
        ))}
      </div>

      <button
        onClick={reload}
        className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
        title="Reload"
        data-testid="shield-hits-reload"
        disabled={loading}
      >
        <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  );
};

export default PrivacyShieldHitsCard;
